import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation } from "react-router-dom";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import IssuerProductsTableComponent from "../../components/tables/products/IssuerProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";

export default function IssuerProductListPage() {
  const { t, n } = useContext(TranslatorContext);
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("new");

  const [allProducts, setAllProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);

  const [listersOptions, setListersOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [storesOptions, setStoresOptions] = useState([]);

  const [selectedLister, setSelectedLister] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  const [counts, setCounts] = useState({
    new_products: 0,
    issued_products: 0,
    approved_products: 0,
  });

  const tableHeaders = [
    "Product Code",
    "Title",
    "Category",
    "Brand",
    "Selling Style",
    "Selling Price $",
    "Quantity",
    "Action",
  ];

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    },
  });

  const extractProducts = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) {
      if (data.length > 0 && data[0].lister_products) {
        let flattened = [];
        data.forEach((item) => {
          const lister = item.lister;
          (item.lister_products || []).forEach((product) => {
            flattened.push({
              ...product,
              lister_id: lister?.id || product?.lister_id,
              lister_name: lister?.name || product?.lister?.name,
            });
          });
        });
        return flattened;
      }
      return data;
    }
    return [];
  };

  const fetchInitialData = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${baseUrl}/issuer/products/initial-data`, getAuthHeader());
      const resData = response.data?.data || response.data;

      if (resData?.listers) setListersOptions(resData.listers);
      if (resData?.categories) setCategoriesOptions(resData.categories);
      if (resData?.stores) setStoresOptions(resData.stores);
    } catch (err) {
      console.error("Error fetching initial data filters:", err);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const [newRes, issuedRes, approvedRes] = await Promise.all([
        axios.get(`${baseUrl}/issuer/products`, getAuthHeader()),
        axios.get(`${baseUrl}/issuer/issued-products`, getAuthHeader()),
        axios.get(`${baseUrl}/issuer/checeker-approved-products`, getAuthHeader()),
      ]);

      setCounts({
        new_products: extractProducts(newRes.data?.data).length,
        issued_products: extractProducts(issuedRes.data?.data).length,
        approved_products: extractProducts(approvedRes.data?.data).length,
      });
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  }, []);

  const fetchTabData = useCallback(async (tab) => {
    setLoading(true);
    setError(null);

    let endpoint = "";
    if (tab === "new") endpoint = "/issuer/products";
    else if (tab === "issued") endpoint = "/issuer/issued-products";
    else if (tab === "approved") endpoint = "/issuer/checeker-approved-products";

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        getAuthHeader()
      );

      const resData = response.data;
      if (!resData.success && resData.success !== undefined) {
        setError(resData.message || "Failed to load products.");
        setAllProducts([]);
        setProductsList([]);
        return;
      }

      const products = extractProducts(resData.data || resData);
      setAllProducts(products);
      setProductsList(products);
    } catch (err) {
      console.error("Error fetching tab data:", err);
      setError(
        err.response?.data?.message || "Failed to fetch data. Please try again."
      );
      setAllProducts([]);
      setProductsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    if (tabParam && ["new", "issued", "approved"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
    fetchInitialData();
    fetchCounts();
  }, [fetchInitialData, fetchCounts]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  useEffect(() => {
    let filtered = [...allProducts];

    if (selectedLister) {
      filtered = filtered.filter(
        (p) => String(p.lister_id || p.user_id || p.lister?.id) === String(selectedLister)
      );
    }
    if (selectedCategory) {
      filtered = filtered.filter(
        (p) => String(p.category_id || p.category?.id) === String(selectedCategory)
      );
    }
    if (selectedStore) {
      filtered = filtered.filter(
        (p) => String(p.store_id || p.store?.id) === String(selectedStore)
      );
    }

    setProductsList(filtered);
  }, [allProducts, selectedLister, selectedCategory, selectedStore]);

  const cardsData = [
    {
      key: "new",
      title: "new_product_count",
      digit: counts.new_products,
      icon: "add_shopping_cart",
      variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
    },
    {
      key: "issued",
      title: "issued_products",
      digit: counts.issued_products,
      icon: "send",
      variant: activeTab === "issued" ? "lg purple active-card" : "lg purple",
    },
    {
      key: "approved",
      title: "checker_approved_products",
      digit: counts.approved_products,
      icon: "check_circle",
      variant: activeTab === "approved" ? "lg green active-card" : "lg green",
    },
  ];

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">{t("issuer_product_list")}</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("products")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{t("issuer_products")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {cardsData.map((card) => (
          <Col key={card.key} sm={6} lg={4}>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => setActiveTab(card.key)}
            >
              <FloatCardComponent
                variant={card.variant}
                digit={n(card.digit)}
                title={t(card.title)}
                icon={card.icon}
              />
            </div>
          </Col>
        ))}

        <Col xl={12}>
          <div className="mc-card">
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            <Row>
              <Col xs={12} sm={6} md={4}>
                <LabelFieldComponent
                  label={t("lister_by")}
                  option={[
                    "All Listers",
                    ...listersOptions.map((l) => l.name || l.username || `Lister ${l.id}`),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Listers") {
                      setSelectedLister("");
                      return;
                    }
                    const selected = listersOptions.find((l) => (l.name || l.username) === value);
                    setSelectedLister(selected?.id || "");
                  }}
                />
              </Col>

              <Col xs={12} sm={6} md={4}>
                <LabelFieldComponent
                  label={t("category_by")}
                  option={[
                    "All Categories",
                    ...categoriesOptions.map((c) => c.name || c.title),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Categories") {
                      setSelectedCategory("");
                      return;
                    }
                    const selected = categoriesOptions.find((c) => (c.name || c.title) === value);
                    setSelectedCategory(selected?.id || "");
                  }}
                />
              </Col>

              <Col xs={12} sm={6} md={4}>
                <LabelFieldComponent
                  label={t("store_by")}
                  option={[
                    "All Stores",
                    ...storesOptions.map((s) => s.name || s.store_name),
                  ]}
                  labelDir="label-col"
                  fieldSize="w-100 h-md mb-4"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "All Stores") {
                      setSelectedStore("");
                      return;
                    }
                    const selected = storesOptions.find((s) => (s.name || s.store_name) === value);
                    setSelectedStore(selected?.id || "");
                  }}
                />
              </Col>

              <Col xl={12}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_products...")}</p>
                  </div>
                ) : (
                  <IssuerProductsTableComponent
                    thead={tableHeaders}
                    tbody={productsList}
                    activeTab={activeTab}
                  />
                )}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}